import { useEffect, useRef, useState } from "react";

/**
 * Detects when a table's Paper/container element has scrolled past the top of a
 * scroll container, indicating that a sticky header is now active.
 *
 * @param scrollContainerId - The `id` of the scrollable ancestor element to listen on.
 * @returns `{ ref, isSticky }` — attach `ref` to the element whose top edge you want
 *   to track (e.g. the Paper wrapping the table), and read `isSticky` to know whether
 *   the sticky header is currently pinned.
 */
export function useStickyTableHeader<T extends HTMLElement = HTMLDivElement>(
  scrollContainerId: string,
) {
  const ref = useRef<T | null>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const scrollContainer = document.getElementById(scrollContainerId);
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!ref.current) return;
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const elementTop = ref.current.getBoundingClientRect().top;
      setIsSticky(elementTop <= containerTop);
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [scrollContainerId]);

  return { ref, isSticky };
}
